import { useQueryClient } from '@tanstack/react-query'
import { getSubgraphConfig, SubgraphType } from 'config/subgraph'
import { ANALYTICS_CHART_START_TIMESTAMP } from 'constants/chart'
import { fetchBlockMap } from 'hooks/backend/blocks'
import { useConnection } from 'hooks/useConnection'
import { useGetV2GlobalChartQuery, useGetV3GlobalChartQuery } from 'queries/analytics/v3/generated'
import { useCallback, useEffect } from 'react'

import { v2ChartQueryKey } from './useV2ChartData'
import { prefetchV2TopPools } from './useV2PoolDatas'
import { prefetchV2TopTokens } from './useV2TokenDatas'
import { v3ChartQueryKey } from './useV3ChartData'
import { prefetchV3TopPools } from './useV3PoolDatas'
import { prefetchV3TopTokens } from './useV3TokenDatas'

type VersionView = 'v2' | 'v3'
type TableKind = 'pools' | 'tokens'

interface PrefetchParams {
  versionView: VersionView
  tableKind: TableKind
  currentReady: boolean
  timestamps?: [number, number, number]
  blocks?: Map<number, number>
  latestIndexedBlockNumber?: number
}

export function useAnalyticsPrefetch({
  versionView,
  tableKind,
  currentReady,
  timestamps,
  blocks,
  latestIndexedBlockNumber,
}: PrefetchParams) {
  const queryClient = useQueryClient()
  const { chainId } = useConnection()

  const resolveEndpoint = useCallback(
    (type: SubgraphType) => {
      try {
        return getSubgraphConfig(chainId, type).endpoint
      } catch (error) {
        console.error(error)
        return undefined
      }
    },
    [chainId]
  )

  const fetchChartForVersion = useCallback(
    async (target: VersionView, params?: { startTime?: number }) => {
      const endpoint = resolveEndpoint(SubgraphType.V3)
      if (!endpoint) return undefined

      if (target === 'v2') {
        const exec = useGetV2GlobalChartQuery.fetcher(
          { endpoint },
          {
            skip: 0,
          }
        )
        return exec()
      }

      const exec = useGetV3GlobalChartQuery.fetcher(
        { endpoint },
        {
          startTime: params?.startTime ?? 0,
          skip: 0,
        }
      )

      return exec()
    },
    [resolveEndpoint]
  )

  const createPrefetchBatches = useCallback(() => {
    const activeKeys = new Set<string>([`${versionView}-chart`, `${versionView}-${tableKind}`])
    const seenKeys = new Set<string>()
    const batches: Array<Array<() => Promise<unknown>>> = []
    let blocksPromise: Promise<Map<number, number>> | undefined
    const chartStartTime = ANALYTICS_CHART_START_TIMESTAMP
    const historyTimestamps = timestamps

    const ensureBlocks = () => {
      if (!historyTimestamps) {
        return Promise.resolve(new Map<number, number>())
      }

      if (blocks) {
        return Promise.resolve(blocks)
      }

      if (!blocksPromise) {
        blocksPromise = fetchBlockMap({ chainId, timestamps: historyTimestamps })
      }
      return blocksPromise
    }

    const tasks = new Map<string, () => Promise<unknown>>()

    tasks.set('v2-chart', () =>
      queryClient.prefetchQuery({
        queryKey: v2ChartQueryKey(chainId, { startTime: chartStartTime }),
        queryFn: () => fetchChartForVersion('v2', { startTime: chartStartTime }),
      })
    )

    tasks.set('v3-chart', () =>
      queryClient.prefetchQuery({
        queryKey: v3ChartQueryKey(chainId, chartStartTime, 0),
        queryFn: () => fetchChartForVersion('v3', { startTime: chartStartTime }),
      })
    )

    if (historyTimestamps) {
      const [t24, t48, tWeek] = historyTimestamps

      const registerBlockTask = (key: string, prefetcher: (blocks: Map<number, number>) => Promise<unknown>) => {
        tasks.set(key, async () => {
          const blocks = await ensureBlocks()
          return prefetcher(blocks)
        })
      }

      registerBlockTask('v2-pools', (blocks) =>
        prefetchV2TopPools(queryClient, chainId, t24, t48, tWeek, blocks, false, latestIndexedBlockNumber)
      )
      registerBlockTask('v2-tokens', (blocks) =>
        prefetchV2TopTokens(queryClient, chainId, t24, t48, tWeek, blocks, latestIndexedBlockNumber)
      )
      registerBlockTask('v3-pools', (blocks) =>
        prefetchV3TopPools(queryClient, chainId, t24, t48, tWeek, blocks, false, latestIndexedBlockNumber)
      )
      registerBlockTask('v3-tokens', (blocks) =>
        prefetchV3TopTokens(queryClient, chainId, t24, t48, tWeek, blocks, latestIndexedBlockNumber)
      )
    }

    const otherVersion: VersionView = versionView === 'v2' ? 'v3' : 'v2'

    const priorityGroups: string[][] = [
      // mirror how useAnalyticsData hydrates: both table kinds for the active version, then charts
      [`${versionView}-pools`, `${versionView}-tokens`],
      [`${versionView}-chart`],
      // finally hydrate the opposite version (chart first so its tables can reuse cache)
      [`${otherVersion}-chart`],
      [`${otherVersion}-pools`, `${otherVersion}-tokens`],
    ]

    const scheduleGroup = (keys: string[]) => {
      const groupTasks: Array<() => Promise<unknown>> = []
      for (const key of keys) {
        if (seenKeys.has(key) || activeKeys.has(key)) continue
        const task = tasks.get(key)
        if (!task) continue
        seenKeys.add(key)
        groupTasks.push(task)
      }
      if (groupTasks.length) {
        batches.push(groupTasks)
      }
    }

    priorityGroups.forEach(scheduleGroup)

    const remaining: Array<() => Promise<unknown>> = []
    for (const [key, task] of tasks.entries()) {
      if (activeKeys.has(key) || seenKeys.has(key)) continue
      remaining.push(task)
    }
    if (remaining.length) {
      batches.push(remaining)
    }

    return batches
  }, [blocks, chainId, fetchChartForVersion, latestIndexedBlockNumber, queryClient, tableKind, timestamps, versionView])

  useEffect(() => {
    if (!currentReady) return undefined

    const batches = createPrefetchBatches()
    if (!batches.length) return undefined

    void (async () => {
      for (const batch of batches) {
        const results = await Promise.allSettled(batch.map((task) => task()))

        results.forEach((result, idx) => {
          if (result.status === 'rejected') {
            console.error(`Prefetch task ${idx} failed:`, result.reason)
          }
        })
      }
    })()
  }, [createPrefetchBatches, currentReady])
}
